import { instanceBo } from "../utils/axios.create";

export interface EnrollmentSummaryFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EnrollmentSummaryKpis {
	totalEnrolled: number;
	active: number;
	completed: number;
	disenrolled: number;
	avgCompletionDays: number;
}

export interface EnrollmentSummaryByOrgUnit {
	orgUnitId: string;
	orgUnit: string;
	active: number;
	completed: number;
	disenrolled: number;
	paused: number;
}

export interface EnrollmentSummaryRow {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	researcher: string;
	startDate: string | null;
	day: number;
	totalDays: number;
	status: "ACTIVE" | "COMPLETED" | "DISENROLLED" | "PAUSED";
	completionPct: number;
}

export interface EnrollmentSummaryFilterOption {
	id: string;
	name: string;
}

export interface EnrollmentSummaryPagination {
	pageNumber: number;
	pageSize: number;
	total: number;
}

export interface EnrollmentSummaryResponse {
	kpis: EnrollmentSummaryKpis;
	byOrgUnit: EnrollmentSummaryByOrgUnit[];
	enrollments: EnrollmentSummaryRow[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getEnrollmentSummary = async (
	filters: EnrollmentSummaryFilters,
): Promise<EnrollmentSummaryResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/enrollment-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface IncentiveDisbursementFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface IncentiveDisbursementKpis {
	totalEarned: number;
	totalReceived: number;
	avgEarned: number;
	potential: number;
	engagementRate: number;
}

export interface IncentiveDisbursementByDay {
	day: number;
	earned: number;
	potential: number;
}

export interface IncentiveDisbursementByEnrollment {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	researcher: string;
	potential: number;
	earned: number;
	received: number;
	engagementRate: number;
}

export interface IncentiveDisbursementResponse {
	kpis: IncentiveDisbursementKpis;
	byDay: IncentiveDisbursementByDay[];
	byEnrollment: IncentiveDisbursementByEnrollment[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getIncentiveDisbursement = async (
	filters: IncentiveDisbursementFilters,
): Promise<IncentiveDisbursementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/incentive-disbursement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EducationalEngagementFilters {
	accountId: string;
	programId: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EducationalEngagementKpis {
	contentItems: number;
	totalAccesses: number;
	avgAccessRate: number;
}

export interface EducationalEngagementByItem {
	itemId: string;
	title: string;
	type: string;
	accesses: number;
	accessRate: number;
}

export interface EducationalEngagementItemFlag {
	itemId: string;
	accessed: boolean;
}

export interface EducationalEngagementByEnrollment {
	enrollmentId: string;
	participantName: string;
	items: EducationalEngagementItemFlag[];
}

export interface EducationalEngagementResponse {
	kpis: EducationalEngagementKpis;
	byItem: EducationalEngagementByItem[];
	byEnrollment: EducationalEngagementByEnrollment[];
	pagination: EnrollmentSummaryPagination;
}

const getEducationalEngagement = async (
	filters: EducationalEngagementFilters,
): Promise<EducationalEngagementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/educational-engagement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventActivityFilters {
	accountId: string;
	programId: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EventActivityEvent {
	eventId: string;
	name: string;
}

export interface EventActivityKpis {
	expected: number;
	submitted: number;
	missed: number;
	pending: number;
}

export type EventActivityStatus = "SUBMITTED" | "MISSED" | "PENDING";

export interface EventActivitySubmission {
	eventId: string;
	status: EventActivityStatus;
}

export interface EventActivityDay {
	day: number;
	submissions: EventActivitySubmission[];
}

export interface EventActivityRow {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	completionPct: number;
	days: EventActivityDay[];
}

export interface EventActivityResponse {
	events: EventActivityEvent[];
	kpis: EventActivityKpis;
	rows: EventActivityRow[];
	pagination: EnrollmentSummaryPagination;
}

const getEventActivity = async (
	filters: EventActivityFilters,
): Promise<EventActivityResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/event-activity", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventRatingsFilters {
	accountId: string;
	programId: string;
	eventId?: string;
	enrollmentId?: string;
	startDate?: string;
	endDate?: string;
}

export type EventRatingType = "SCORED_SUM" | "INDEPENDENT_RATINGS";

export interface EventRatingsEvent {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
}

export interface EventRatingsEnrollment {
	enrollmentId: string;
	participantName: string;
}

export interface ScoredSumDay {
	day: number;
	avgScore: number | null;
	submissionCount: number;
}

export interface ScoredSumKpis {
	avgScoreDay1: number | null;
	avgScoreDay14: number | null;
	improvementPct: number | null;
	submissionRate: number;
}

export interface ScoredSumPayload {
	scoreMin: number;
	scoreMax: number;
	avgScoreByDay: ScoredSumDay[];
	kpis: ScoredSumKpis;
}

export interface IndependentDistressDay {
	day: number;
	avgDistress: number | null;
	submissionCount: number;
}

export interface IndependentEmojiDay {
	day: number;
	counts: Record<string, number>;
}

export interface IndependentEmojiPick {
	value: string;
	count: number;
}

export interface IndependentKpis {
	avgDistressDay1: number | null;
	avgDistressDay14: number | null;
	mostCommonEmojiDay1: IndependentEmojiPick | null;
	submissionRate: number;
}

export interface IndependentRatingsPayload {
	distressField: { name: string; min: number; max: number };
	emojiField: { name: string; values: string[] };
	avgDistressByDay: IndependentDistressDay[];
	emojiCountsByDay: IndependentEmojiDay[];
	kpis: IndependentKpis;
}

export interface EventRatingsChart {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
	scoredSum?: ScoredSumPayload;
	independent?: IndependentRatingsPayload;
}

export interface EventRatingsResponse {
	events: EventRatingsEvent[];
	enrollments: EventRatingsEnrollment[];
	chart: EventRatingsChart | null;
}

const getEventRatings = async (
	filters: EventRatingsFilters,
): Promise<EventRatingsResponse> => {
	const params: Record<string, string> = {
		programId: filters.programId,
	};
	if (filters.eventId) params.eventId = filters.eventId;
	if (filters.enrollmentId) params.enrollmentId = filters.enrollmentId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;

	return instanceBo
		.get("/reports/event-ratings", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// ---------------------------------------------------------------------------
// SIMS-6960 — Enrollment Summary (configurable events). Native (no-Qlik)
// report: one row per enrollment with lifetime, event-config-driven result
// columns. Backend-paginated and scoped by accountId + isDeleted.
// ---------------------------------------------------------------------------

export interface EnrollmentSummaryConfigurableFilters {
	accountId: string;
	programId: string;
	status?: string;
	orgUnitId?: string;
	providerId?: string;
	filterSummary?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface ResultColumn {
	key: string;
	label: string;
}

export interface ReportFilterOption {
	id: string;
	name: string | null;
}

export interface EnrollmentSummaryConfigurableRow {
	enrollmentId: string;
	beneficiaryName: string | null;
	cin: string | null;
	site: string | null;
	provider: string | null;
	npi: string | null;
	startDate: string | null;
	endDate: string | null;
	status: string;
	payerPlanName: string | null;
	payerPlanNumber: string | null;
	resultCounts: Record<string, number>;
	incentiveEarned: number;
	incentiveDisbursed: number;
	bankBalance: number;
}

export interface EnrollmentSummaryConfigurableResponse {
	columns: ResultColumn[];
	rows: EnrollmentSummaryConfigurableRow[];
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getEnrollmentSummaryConfigurable = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<EnrollmentSummaryConfigurableResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.status) params.status = filters.status;
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/enrollment-summary-configurable", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// Backend-generated Excel (exceljs) for the configurable Enrollment Summary.
const getEnrollmentSummaryConfigurableExcel = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<Blob> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.status) params.status = filters.status;
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.filterSummary) params.filterSummary = filters.filterSummary;

	return instanceBo
		.get("/reports/enrollment-summary-configurable/export", {
			params,
			responseType: "blob",
		})
		.then((res) => res.data as Blob)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// Fetch every filtered row (paged, capped) for the client-side PDF export.
const fetchAllEnrollmentSummaryConfigurable = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<{
	columns: ResultColumn[];
	rows: EnrollmentSummaryConfigurableRow[];
}> => {
	const PAGE_SIZE = 100;
	const EXPORT_CAP = 5000;
	let pageNumber = 0;
	let columns: ResultColumn[] = [];
	const rows: EnrollmentSummaryConfigurableRow[] = [];
	for (;;) {
		const res = await getEnrollmentSummaryConfigurable({
			...filters,
			pageNumber,
			pageSize: PAGE_SIZE,
		});
		if (pageNumber === 0) columns = res.columns;
		rows.push(...res.rows);
		pageNumber += 1;
		if (
			res.rows.length === 0 ||
			rows.length >= res.pagination.total ||
			rows.length >= EXPORT_CAP
		) {
			break;
		}
	}
	return { columns, rows };
};

// ---------------------------------------------------------------------------
// SIMS-5596 — Program Activity Summary (KPI report).
// ---------------------------------------------------------------------------

export interface ProgramActivitySummaryFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string;
	payerIds?: string;
	groupLabelIds?: string;
	providerId?: string;
	startDate?: string;
	endDate?: string;
}

export interface VisitResultBucket {
	key: string;
	label: string;
	count: number;
}

export interface VisitResultsByEvent {
	eventId: string;
	eventName: string | null;
	buckets: VisitResultBucket[];
}

export interface ProgramActivitySummaryResponse {
	labels: { singular: string; plural: string };
	enrollmentCounts: {
		enrolled: number;
		active: number;
		completed: number;
		disenrolled: number;
	};
	incentiveSummary: {
		totalEarned: number;
		earnedTxnCount: number;
		totalDisbursed: number;
		disbursedTxnCount: number;
	};
	visitResultsByEvent: VisitResultsByEvent[];
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
}

const getProgramActivitySummary = async (
	filters: ProgramActivitySummaryFilters,
): Promise<ProgramActivitySummaryResponse> => {
	const params: Record<string, string> = { programId: filters.programId };
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.payerIds) params.payerIds = filters.payerIds;
	if (filters.groupLabelIds) params.groupLabelIds = filters.groupLabelIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;

	return instanceBo
		.get("/reports/program-activity-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// ---------------------------------------------------------------------------
// SIMS-5663 — Visit Detail (one row per visit) + lazy analyte drill-down.
// ---------------------------------------------------------------------------

export interface VisitDetailFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string;
	payerIds?: string;
	groupLabelIds?: string;
	providerId?: string;
	startDate?: string;
	endDate?: string;
	cin?: string;
	filterSummary?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface VisitDetailRow {
	enrollmentId: string;
	udtResultId: string;
	visitDate: string | null;
	visitNumber: number | null;
	member: string | null;
	cin: string | null;
	orgUnit: string | null;
	provider: string | null;
	eventId: string | null;
	eventName: string | null;
	result: string | null;
	earned: number;
	payerPlanName: string | null;
	hasAnalyteDetails: boolean;
}

export interface VisitDetailResponse {
	rows: VisitDetailRow[];
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

export interface AnalyteResult {
	shortName: string | null;
	scientificName: string | null;
	positive: boolean;
}

export interface VisitAnalyteResponse {
	cupName: string | null;
	isTampered: boolean;
	tamperedReasons: string[];
	analytes: AnalyteResult[];
}

const buildVisitDetailParams = (
	filters: VisitDetailFilters,
): Record<string, string | number> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.payerIds) params.payerIds = filters.payerIds;
	if (filters.groupLabelIds) params.groupLabelIds = filters.groupLabelIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.cin) params.cin = filters.cin;
	if (filters.filterSummary) params.filterSummary = filters.filterSummary;
	return params;
};

const getVisitDetail = async (
	filters: VisitDetailFilters,
): Promise<VisitDetailResponse> => {
	const params = buildVisitDetailParams(filters);
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/visit-detail", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

const getVisitAnalytes = async (
	accountId: string,
	enrollmentId: string,
	udtResultId: string,
): Promise<VisitAnalyteResponse> =>
	instanceBo
		.get(`/reports/visit-detail/${enrollmentId}/${udtResultId}/analytes`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

// Backend-generated Excel (exceljs) — one row per visit-analyte.
const getVisitDetailExcel = async (
	filters: VisitDetailFilters,
): Promise<Blob> =>
	instanceBo
		.get("/reports/visit-detail/export", {
			params: buildVisitDetailParams(filters),
			responseType: "blob",
		})
		.then((res) => res.data as Blob)
		.catch((err) => Promise.reject(err.response?.data ?? err));

// Fetch every filtered visit row (paged, capped) for the client-side PDF.
const fetchAllVisitDetail = async (
	filters: VisitDetailFilters,
): Promise<VisitDetailRow[]> => {
	const PAGE_SIZE = 100;
	const EXPORT_CAP = 5000;
	let pageNumber = 0;
	const rows: VisitDetailRow[] = [];
	for (;;) {
		const res = await getVisitDetail({
			...filters,
			pageNumber,
			pageSize: PAGE_SIZE,
		});
		rows.push(...res.rows);
		pageNumber += 1;
		if (
			res.rows.length === 0 ||
			rows.length >= res.pagination.total ||
			rows.length >= EXPORT_CAP
		) {
			break;
		}
	}
	return rows;
};

export const reportsService = {
	getEnrollmentSummary,
	getIncentiveDisbursement,
	getEducationalEngagement,
	getEventActivity,
	getEventRatings,
	getEnrollmentSummaryConfigurable,
	getEnrollmentSummaryConfigurableExcel,
	fetchAllEnrollmentSummaryConfigurable,
	getProgramActivitySummary,
	getVisitDetail,
	getVisitAnalytes,
	getVisitDetailExcel,
	fetchAllVisitDetail,
};
